import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import MarkdownIt from "markdown-it";
import MdEditor from "react-markdown-editor-lite";

import styles from "./style.module.css";
import { CMSTypes } from "../../interfaces";
import { FAQSchema } from "../../features/cms/validations";
import { CONSTANTS } from "../../lib/constants/messages";
import { uploadFile } from "../../features/blogs/api";
import { updateFAQ, createFAQ, updateTroubleShoot, createTroubleShoot } from "../../features/cms/api";
import { addCMSCountry, updateCountryBlogs, updateCountryInsta, updateCountrySEO } from "../../features/cmscountry/api";
import { useParams } from "react-router-dom";
import { CMS_TYPE } from "../../lib/constants";
import { uploadOgImage } from "../../features/deeplink/api";

interface PopupType {
  show: boolean;
  handleClose: () => void;
  getFaqs: () => void;
  faq: any | null;
  typeOfPage?:number;
  cmsId?:string;
  faqIndex?:number;
  type:string;
  seoValue:any;
  SeoData:any
};

const CreateUpdateSeo = (props: PopupType) => {  
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [multipleValues, setMultipleValues] = useState<string[]>([]);
  const [currentInputValue, setCurrentInputValue] = useState("");
  const { id } = useParams<{ id: string }>();

  const mdParser = new MarkdownIt({
    html: true,
    linkify: true,
    typographer: true,
  });

  // Check if current type supports multiple values
  const isMultipleValueType = props.type === "subheadings" || props.type === "secondaryKeywords";
  const isImageType = props.type === "ogSecureUrl";
  const isMarkupType = props.type === "markup";

  // Normalize incorrectly escaped string values stored in DB
  // e.g. `"<script type=""application/ld+json"">` → `<script type="application/ld+json">`
  const normalizeStringValue = (value: string): string => {
    let result = value;
    if (result.startsWith('"') && result.endsWith('"')) {
      result = result.slice(1, -1);
    }
    result = result.replace(/""/g, '"');
    return result;
  };
  
  const faqFormik = useFormik({
    initialValues:{
      value: "",
    },
    onSubmit: (values) => {
      let seoValue;
      if (isMultipleValueType) {
        seoValue = multipleValues;
      } else if (isImageType) {
        seoValue = uploadedImage;
      } else if (isMarkupType) {
        // markup is an HTML string — send as plain string, no JSON.parse
        seoValue = values.value;
      } else {
        // For object-type fields: try JSON.parse to send as proper object
        // avoids manual/incorrect quote escaping — axios will JSON.stringify cleanly
        try {
          const parsed = JSON.parse(values.value);
          seoValue = parsed;
        } catch {
          seoValue = values.value;
        }
      }
      
      const payload = {
        seoFields: {
          [props.type]: seoValue,
        },
        countryId: id,
        type: props.typeOfPage,
      }

      const getPayload = props.faq ? {
        seoFields: {
          ...props.SeoData,
          [props.type]: seoValue,
        },
        cmsId: props.cmsId,
      } : payload;

      toast.promise(
        updateCountrySEO(getPayload),
        {
          pending: {
            render() {
              return props.faq ? CONSTANTS.CMS.UPDATING : "Adding CMS";
            }
          },
          success: {
            render() {
              props.getFaqs();
  
              if (!props.faq) faqFormik.resetForm();
  
              props.handleClose();
              return props.faq ? CONSTANTS.CMS.SUCCESS : "Successfully Added";
            }
          },
          error: {
            render() {
              return props.faq ? CONSTANTS.CMS.FAILED : "Failed to Add";
            }
          }
        }
      )
    },
  });

  // Functions for handling multiple values
  const addValue = () => {
    if (currentInputValue.trim() && !multipleValues.includes(currentInputValue.trim())) {
      setMultipleValues([...multipleValues, currentInputValue.trim()]);
      setCurrentInputValue("");
    }
  };

  const removeValue = (index: number) => {
    const newValues = multipleValues.filter((_, i) => i !== index);
    setMultipleValues(newValues);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addValue();
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImageUploading(true);
    const formData = new FormData();
    formData.append("image", file);

    if (isImageType) {
      uploadOgImage(formData)
        .then((res: any) => {
          const imageUrl = res.url;
          setUploadedImage(imageUrl);
          faqFormik.setFieldValue("value", imageUrl);
          setImageUploading(false);
          toast.success("Image uploaded successfully!");
        })
        .catch(error => {
          setImageUploading(false);
          toast.error("Failed to upload image");
          console.error("Image upload error:", error);
        });
    }
  };

  const removeImage = () => {
    setUploadedImage(null);
    faqFormik.setFieldValue("value", "");
  };

  const closePopup = () => {
    props.handleClose();
    faqFormik.resetForm();
    setUploadedImage(null);
    setMultipleValues([]);
    setCurrentInputValue("");
  };

  useEffect(() => {
    if (props.seoValue) {
      if (isMultipleValueType) {
        // For subheadings and secondaryKeywords, expect array values
        if (Array.isArray(props.seoValue)) {
          setMultipleValues(props.seoValue);
        } else if (props.seoValue && typeof props.seoValue === 'string') {
          // Handle case where backend sends string instead of array
          setMultipleValues([props.seoValue]);
        } else {
          setMultipleValues([]);
        }
      } else if (isImageType) {
        setUploadedImage(props.seoValue);
        faqFormik.setFieldValue("value", props.seoValue);
      } else {
        // For single value fields — if value is object/array, stringify properly
        const rawValue = props.seoValue;
        let displayValue: string;
        if (rawValue !== null && rawValue !== undefined && typeof rawValue === "object") {
          displayValue = JSON.stringify(rawValue, null, 2);
        } else if (isMarkupType && typeof rawValue === "string") {
          // Normalize corrupted ""double-quote"" escaping in markup/JSON-LD strings
          displayValue = normalizeStringValue(rawValue);
        } else {
          displayValue = rawValue || "";
        }
        faqFormik.setValues({ value: displayValue });
      }
    } else {
      // Reset to empty state when no seoValue
      if (isMultipleValueType) {
        setMultipleValues([]);
      } else {
        faqFormik.setValues({ value: "" });
      }
      if (isImageType) {
        setUploadedImage(null);
      }
    }
  }, [props.seoValue, props.type, isMultipleValueType, isImageType])

  return (
    <Modal size="xl" show={props.show} onHide={props.handleClose}>
      <Modal.Header className={styles.modHead}>
        <Modal.Title>{props.type}</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.faqContent}>
        {isMultipleValueType ? (
          <div className="mb-3">
            <label className="w-100 mb-1">
              {props.type === "subheadings" ? "Subheadings" : "Secondary Keywords"}
            </label>
            <div className="d-flex gap-2 mb-2">
              <input
                className={styles.input}
                type="text"
                placeholder={`Add ${props.type === "subheadings" ? "subheading" : "keyword"}`}
                value={currentInputValue}
                onChange={(e) => setCurrentInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={addValue}
                disabled={!currentInputValue.trim()}
              >
                Add
              </button>
            </div>
            
            {multipleValues.length > 0 && (
              <div className="mt-3">
                <label className="mb-2">Added {props.type}:</label>
                <div className="d-flex flex-wrap gap-2">
                  {multipleValues.map((value, index) => (
                    <div key={index} className="badge bg-secondary d-flex align-items-center gap-2 p-2">
                      <span>{value}</span>
                      <button
                        type="button"
                        className="btn-close btn-close-white"
                        style={{ fontSize: '0.7em' }}
                        onClick={() => removeValue(index)}
                        aria-label="Remove"
                      ></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {multipleValues.length === 0 && (
              <small className="text-muted">
                No {props.type === "subheadings" ? "subheadings" : "keywords"} added yet. 
                Type and press Enter or click Add button.
              </small>
            )}
          </div>
        ) : isImageType ? (
          <div className="mb-3">
            <label className="w-100 mb-1">
              Upload Image
              {
                faqFormik.touched.value && faqFormik.errors.value ?
                  <span className="error">{faqFormik.errors.value}</span>
                  :
                  null
              }
            </label>
            <div className="d-flex gap-2 align-items-start">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={imageUploading}
                className={styles.input}
              />
              {imageUploading && <div className="spinner-border text-primary" role="status" />}
            </div>
            {uploadedImage && (
              <div className="mt-3">
                <img src={uploadedImage} alt="Uploaded" style={{ maxWidth: '200px' }} className="mb-2" />
                <button
                  type="button"
                  className="btn btn-danger btn-sm d-block"
                  onClick={removeImage}
                >
                  Remove Image
                </button>
              </div>
            )}
          </div>
        ) : isMarkupType ? (
          <div className="mb-3">
            <label className="w-100 mb-1">
              Markup / JSON-LD Schema
              {faqFormik.touched.value && faqFormik.errors.value && (
                <span className="error">{faqFormik.errors.value}</span>
              )}
            </label>
            <textarea
              className={styles.input}
              placeholder={`<script type="application/ld+json">\n{\n  "@context": "https://schema.org",\n  ...\n}\n</script>`}
              name="value"
              rows={14}
              style={{ fontFamily: "monospace", fontSize: "13px", width: "100%", resize: "vertical" }}
              onChange={faqFormik.handleChange}
              onBlur={faqFormik.handleBlur}
              value={faqFormik.values.value}
            />
          </div>
        ) : (
          <div className="mb-3">
            <label className="w-100 mb-1">
              Value
              {
                faqFormik.touched.value && faqFormik.errors.value ?
                  <span className="error">{faqFormik.errors.value}</span>
                  :
                  null
              }
            </label>
            <input
              className={styles.input}
              type="text"
              placeholder="Value"
              name="value"
              onChange={faqFormik.handleChange}
              onBlur={faqFormik.handleBlur}
              value={faqFormik.values.value}
            />
          </div>
        )}
        
      </Modal.Body>
      <Modal.Footer className={styles.modFoter}>
        <button type="button" className={styles.close} onClick={closePopup}>Close</button>
        <button 
          type="submit" 
          className={styles.button} 
          onClick={faqFormik.submitForm}
          disabled={
            (isMultipleValueType && multipleValues.length === 0) ||
            (isImageType && !uploadedImage) ||
            imageUploading
          }
        >
          Save Changes
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateUpdateSeo;